import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MdReceipt, MdThumbUp, MdThumbDown } from 'react-icons/md';
import { authenticatedFetch, resolveFileUrl } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../shared/Toast';
import { FileDownloadLink } from '../shared/FileDownloadLink';

interface AdminBuyerInvoicesSectionProps {
  poId: string;
  po: any;

   /** Which invoices to show in this card. Default keeps both (legacy). */
   variant?: 'all' | 'proforma' | 'final';

}

export function AdminBuyerInvoicesSection({ poId, po, variant = 'all' }: AdminBuyerInvoicesSectionProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectModal, setRejectModal] = useState<{ invoiceId: string; invoiceNumber: string } | null>(
    null
  );
  const [rejectReason, setRejectReason] = useState('');

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['admin-procurement-invoices', poId],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/invoices`);
      if (!res.ok) return [];
      const json = await res.json();
      return json.data || [];
    },
    enabled: !!poId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-procurement-invoices', poId] });
    queryClient.invalidateQueries({ queryKey: ['admin-po-details'] });
    queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-po-enriched'] });
    queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-purchase-order'] });
    queryClient.invalidateQueries({ queryKey: ['admin-buyer-payment-schedule', poId] });
  };

  const approveMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await authenticatedFetch(`/api/v1/admin/procurement-invoices/${invoiceId}/approve`, {
        method: 'POST',
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to approve invoice');
      return err;
    },
    onSuccess: (data) => {
      invalidate();
      const approvedType = data?.data?.invoice?.invoiceType;
      if (approvedType === 'consolidated' || data?.data?.purchaseOrder?.metadata?.paymentUnlocked) {
        showToast('Final invoice accepted. Payment is unlocked.', 'success');
      } else {
        showToast('Proforma accepted. Choose vendor shipping or self shipment next.', 'success');
      }
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ invoiceId, reason }: { invoiceId: string; reason: string }) => {
      const res = await authenticatedFetch(`/api/v1/admin/procurement-invoices/${invoiceId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to reject invoice');
      return err;
    },
    onSuccess: () => {
      invalidate();
      setRejectModal(null);
      setRejectReason('');
      showToast('Invoice rejected. Vendor will be notified.', 'info');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const paymentUnlocked =
    po?.metadata?.paymentUnlocked === true ||
    invoices.some((i: any) => i.invoiceType === 'consolidated' && i.status === 'approved');

  const proforma =
    invoices.find((i: any) => i.invoiceType === 'proforma' && i.status !== 'rejected') ||
    invoices.find((i: any) => i.invoiceType === 'proforma');
  const consolidated =
    invoices.find((i: any) => i.invoiceType === 'consolidated' && i.status !== 'rejected') ||
    invoices.find((i: any) => i.invoiceType === 'consolidated');

  if (isLoading) return null;

  const showProforma = variant === 'all' || variant === 'proforma';
const showFinal = variant === 'all' || variant === 'final';
const invoicesToShow = [
  ...(showProforma && proforma ? [proforma] : []),
  ...(showFinal && consolidated ? [consolidated] : []),
];
// Hide empty final-only card until a final invoice exists
if (variant === 'final' && !consolidated) return null;
const sectionTitle =
  variant === 'final' ? 'Final Invoice' : variant === 'proforma' ? 'Invoices' : 'Invoices';
const sectionBadge =
  variant === 'final'
    ? paymentUnlocked
      ? 'Payment Unlocked'
      : 'Awaiting Acceptance'
    : paymentUnlocked
      ? 'Payment Unlocked'
      : 'Proforma Review';
return (
  <div className="rounded-2xl border border-[#0088cc]/20 bg-[hsl(var(--card))] shadow-sm overflow-hidden">
    <div className="px-6 py-5 border-b border-[#0088cc]/15 flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-base font-bold flex items-center gap-2">
        <MdReceipt className="w-5 h-5 text-[#0088cc]" />
        {sectionTitle}
      </h2>
      <span
        className={cn(
          'text-[10px] font-bold px-2.5 py-1 rounded-full uppercase',
          paymentUnlocked ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
        )}
      >
        {sectionBadge}
      </span>
    </div>
    <div className="p-6 space-y-4">
      {showProforma && !proforma && (
        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
          Waiting for the vendor to send the proforma invoice. After you accept it, choose shipping
          (vendor charges or self shipment with AWB).
        </p>
      )}
      
      {invoicesToShow.map((invoice: any) => {
          const isProforma = invoice.invoiceType === 'proforma';
          const canReview = invoice.status === 'submitted';
          return (
            <div
              key={invoice._id}
              className="p-5 rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5 space-y-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold text-[#0088cc]/65 uppercase tracking-wider">
                    {isProforma ? 'Proforma Invoice' : 'Final Invoice'}
                  </p>
                  <p className="font-bold">{invoice.invoiceNumber}</p>
                </div>
                <span
                  className={cn(
                    'text-[10px] font-bold px-2.5 py-1 rounded-full uppercase',
                    invoice.status === 'approved'
                      ? 'bg-emerald-100 text-emerald-800'
                      : invoice.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                  )}
                >
                  {invoice.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span>
                  {invoice.currency || po?.currency || 'USD'}{' '}
                  {Number(invoice.grandTotal || invoice.totalAmount || 0).toFixed(2)}
                </span>
                {(invoice.documents?.length ? invoice.documents : [{ fileUrl: null }]).slice(0, 3).map((doc: any, idx: number) => (
                  <FileDownloadLink
                    key={idx}
                    href={
                      doc.fileUrl &&
                      (doc.fileUrl.startsWith('http://') ||
                       doc.fileUrl.startsWith('https://') ||
                       doc.fileUrl.startsWith('uploads/') ||
                       doc.fileUrl.startsWith('/uploads/'))
                        ? resolveFileUrl(doc.fileUrl)
                        : `/api/v1/admin/procurement-invoices/${invoice._id}/pdf`
                    }
                    fileName={doc.fileName || `${invoice.invoiceNumber}.pdf`}
                    className="text-[#0088cc] font-semibold hover:underline"
                    onError={(message) => showToast(message, 'error')}
                  />
                ))}
              </div>
              {canReview && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(invoice._id)}
                    className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                  >
                    <MdThumbUp className="h-4 w-4" /> Accept
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setRejectModal({
                        invoiceId: invoice._id,
                        invoiceNumber: invoice.invoiceNumber,
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-rose-800"
                  >
                    <MdThumbDown className="h-4 w-4" /> Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {rejectModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border bg-[hsl(var(--card))] p-6 space-y-4">
            <h3 className="font-bold">Reject {rejectModal.invoiceNumber}</h3>
            <textarea
              className="w-full rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
              rows={3}
              placeholder="Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectModal(null)}
                className="rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                onClick={() =>
                  rejectMutation.mutate({
                    invoiceId: rejectModal.invoiceId,
                    reason: rejectReason.trim(),
                  })
                }
                className="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
